import React, { useRef, useState } from 'react';
import GoogleMapReact from 'google-map-react';
import * as Tooltip from '@radix-ui/react-tooltip';
import { Title, Description } from '@radix-ui/react-dialog';
import { formatCurrency } from '$utilities/data';

import { ShareButtonModal } from '$partials/ui/share-button.jsx';
import { useExchangeRate } from '$contexts/exchange-rate';
import { Dialog } from '$partials/ui';
import { PropertyAreaText, PropertyPriceText } from '$partials/properties';
import getGoogleMapsKey from '$utilities/google-maps-key';
import IconClose from '$partials/icons/icon-close';
import IconMarker from '$partials/icons/icon-marker.jsx';
import IconSubtract from '$partials/icons/icon-subtract';
import IconAdd from '$partials/icons/icon-add';

/* ========================================================================= */
/* Helpers */
/* ========================================================================= */

const hasHoverSupport = () => {
    try {
        return matchMedia('(hover: hover) and (pointer: fine)').matches;
    } catch {
        // Assume that if browser too old to support matchMedia it's likely not a touch device
        return true;
    }
};

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/properties').PropertyInfo}
 */
const PropertyInfo = ({
    __typename,
    displayAddress,
    title,
    price: priceProp,
    secondPrice: secondPriceProp,
    priceCurrency: priceCurrencyProp,
    priceTypeLabel,
    location,
    bedrooms,
    bathrooms,
    tenure,
    propertyReferenceId,
    livingAreaMeters,
    outdoorAreaMeters,
    priceRentPeriod: priceRentPeriodProp,
    propertiesCount,
}) => {
    const { currency, convertToCurrency } = useExchangeRate() || {};

    const [tooltipOpen, setTooltipOpen] = useState(false);
    const hasHover = hasHoverSupport();
    const price = priceProp;
    const secondPrice = secondPriceProp;
    const propertyType = __typename;
    const pageType = propertyType === 'NewHomeDetailPage' ? 'SalePropertyDetailPage' : propertyType;

    const priceCurrency = priceCurrencyProp;
    const priceRentPeriod = priceRentPeriodProp;
    const livingArea = Number(livingAreaMeters);
    const outdoorArea = Number(outdoorAreaMeters);
    const tooltipCurrencies = ['GBP', 'EUR', 'USD', 'ILS'];

    /**
 * @param {string | number | undefined} v
 */
    const shouldRenderStringOrNumber = (v) => {
        if (v) {
            return true;
        }
        return false;
    };

    return (
        <section className='w-full md:max-w-[380px] theme-light-alt'>
            <div className='bg-main-background h-full'>
                <div className='flex flex-col h-full justify-between p-16 md:px-28 md:pt-80'>
                    <div className='flex md:gap-y-48 flex-col col-wrapper'>
                        <div className='col-padding flex flex-col'>
                            {displayAddress && (
                                <p className='type-style-eyebrow mb-8'>
                                    {displayAddress}
                                </p>
                            )}
                            <h1 className='type-style-4'>
                                {title}
                            </h1>

                            {propertyReferenceId && (
                                <p className='hidden md:block type-style-eyebrow mb-8 mt-24'>
                                    {`Property Ref: ${propertyReferenceId}`}
                                </p>
                            )}
                        </div>
                        <div className='flex flex-col justify-between col-padding'>
                            {/* Prefix */}
                            {(priceTypeLabel && priceTypeLabel !== 'Price On Application') && (
                                <p className='type-style-eyebrow mb-8 mt-32 md:mt-0'>
                                    {priceTypeLabel}
                                </p>
                            )}
                            {!priceTypeLabel && (
                                <div className='sm:mb-[25px]' />
                            )}
                            <div className='inline-flex gap-8 items-baseline'>
                                <p className='type-style-4'>
                                    <PropertyPriceText
                                        pageType={pageType}
                                        price={price}
                                        priceCurrency={priceCurrency}
                                        priceRentPeriod={priceRentPeriod}
                                        priceTypeLabel={priceTypeLabel}
                                        secondPrice={secondPrice} />
                                </p>

                                {(propertyType === 'SalePropertyDetailPage' && price) && (
                                    <Tooltip.Provider delayDuration={!hasHover ? 0 : 200}>
                                        <Tooltip.Root
                                            onOpenChange={setTooltipOpen}
                                            open={tooltipOpen}>
                                            <Tooltip.Trigger
                                                onClick={(e) => {
                                                    if (!hasHover) {
                                                        e.preventDefault();
                                                        setTooltipOpen(true);
                                                    }
                                                }}>
                                                <svg
                                                    aria-hidden={true}
                                                    fill='none'
                                                    height='20'
                                                    viewBox='0 0 20 20'
                                                    width='20'
                                                    xmlns='http://www.w3.org/2000/svg'>
                                                    <path
                                                        d='M10 .4a9.6 9.6 0 0 1 9.6 9.6 9.6 9.6 0 0 1-9.6 9.6A9.6 9.6 0 0 1 .4 10 9.6 9.6 0 0 1 10 .4Z'
                                                        stroke='#242320'
                                                        strokeWidth='.8' />
                                                    <path
                                                        d='M7.894 14.167a.38.38 0 0 1-.28-.114.38.38 0 0 1-.114-.28v-.157a.4.4 0 0 1 .036-.164.5.5 0 0 1 .098-.146q.534-.465.825-.818.291-.355.291-1.119 0-.135-.013-.262a1.3 1.3 0 0 0-.054-.258h-.844a.33.33 0 0 1-.24-.099.33.33 0 0 1-.099-.24q0-.139.1-.239a.33.33 0 0 1 .239-.099h.504q-.486-.619-.665-1.1a2.6 2.6 0 0 1-.178-.908q0-1.077.73-1.808a2.42 2.42 0 0 1 1.783-.731q.697 0 1.261.33t.914.916a.3.3 0 0 1 .032.27.3.3 0 0 1-.179.2.3.3 0 0 1-.243.01.4.4 0 0 1-.198-.163 2 2 0 0 0-.68-.647 1.8 1.8 0 0 0-.907-.239q-.771 0-1.303.532a1.76 1.76 0 0 0-.533 1.291q0 .478.25.946.251.467.727 1.101h1.81q.14 0 .239.1.1.099.1.239a.33.33 0 0 1-.1.24.33.33 0 0 1-.24.098h-1.59q.03.164.043.288.01.125.011.297 0 .578-.239 1.106t-.698.95h2.942q.369 0 .612-.192.243-.191.377-.574a.4.4 0 0 1 .133-.195.35.35 0 0 1 .219-.068q.134 0 .243.1a.3.3 0 0 1 .108.233q0 .59-.587.98-.587.393-1.105.393z'
                                                        fill='#242320' />
                                                </svg>
                                            </Tooltip.Trigger>
                                            <Tooltip.Portal>
                                                <Tooltip.Content className='bg-button-primary-filled-background text-button-primary-filled-text rounded min-w-[145px]'>
                                                    <div className='px-16 py-12 flex flex-col gap-4 type-style-eyebrow'>
                                                        {tooltipCurrencies.map((c) => {
                                                            if (c === currency) { return null; }
                                                            return (
                                                                <p key={c}>
                                                                    {formatCurrency(convertToCurrency({
                                                                        to: /** @type {any} */ (c),
                                                                        amount: price
                                                                    }), c)}
                                                                </p>
                                                            );
                                                        })}
                                                    </div>
                                                </Tooltip.Content>
                                            </Tooltip.Portal>
                                        </Tooltip.Root>
                                    </Tooltip.Provider>
                                )}
                            </div>
                            <div className='hidden md:grid grid-cols-2 md:gap-x-8 md:gap-y-24 mt-24 md:mb-32'>
                                {shouldRenderStringOrNumber(propertiesCount) && (
                                    <div>
                                        <p className='type-style-eyebrow'>
                                            Properties
                                        </p>
                                        <p className='type-style-body-r'>
                                            {propertiesCount}
                                        </p>
                                    </div>
                                )}
                                {shouldRenderStringOrNumber(bedrooms) && (
                                    <div>
                                        <p className='type-style-eyebrow'>
                                            Bed
                                        </p>
                                        <p className='type-style-body-r'>
                                            {bedrooms}{__typename === 'NewHomeDetailPage'? '+':''}
                                        </p>
                                    </div>
                                )}
                                {shouldRenderStringOrNumber(bathrooms) && (
                                    <div>
                                        <p className='type-style-eyebrow'>
                                            Bath
                                        </p>
                                        <p className='type-style-body-r'>
                                            {bathrooms}{__typename === 'NewHomeDetailPage'? '+':''}
                                        </p>
                                    </div>
                                )}
                                {tenure?.name && (
                                    <div>
                                        <p className='type-style-eyebrow'>
                                            Tenure
                                        </p>
                                        <p className='type-style-body-r'>
                                            {tenure.name}
                                        </p>
                                    </div>
                                )}
                                {Number.isFinite(livingArea) && livingArea > 0 && (
                                    <div>
                                        <p className='type-style-eyebrow'>
                                            INT.
                                        </p>
                                        <p className='type-style-body-r'>
                                            <PropertyAreaText
                                                areaMeters={livingArea}
                                                suffix={__typename === 'NewHomeDetailPage' ? '+' : ''} />
                                        </p>
                                    </div>
                                )}
                                {Number.isFinite(outdoorArea) && outdoorArea > 0 && (
                                    <div>
                                        <p className='type-style-eyebrow'>
                                            EXT.
                                        </p>
                                        <p className='type-style-body-r'>
                                            <PropertyAreaText
                                                areaMeters={outdoorArea}
                                                suffix={__typename === 'NewHomeDetailPage' ? '+' : ''} />
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className='hidden md:block'>
                        <ShareButtonModal
                            height='h-[44px]'
                            propertyRef={propertyReferenceId}
                            title={title}
                            url={location}
                            width='w-[44px]'/>
                    </div>
                </div>
            </div>
        </section>
    );
};


/**
 * @type {import('$types/components/partials/properties.d.ts').PropertyDetailMapDialog}
 */
const PropertyDetailMapDialog = ({
    setShowMap,
    showMap,
    property
}) => {
    /** @type {React.MutableRefObject<google.maps.Map>} */
    const mapRef = useRef();
    /**
     * Zoom in map
     */
    const handleZoomIn = () => {
        if (mapRef.current) {
            mapRef.current.setZoom(mapRef.current.getZoom() + 1);
        }
    };

    /**
     * Zoom out map
     */
    const handleZoomOut = () => {
        if (mapRef.current) {
            mapRef.current.setZoom(mapRef.current.getZoom() - 1);
        }
    };

    return (
        <>
            <Dialog
                className='theme-light'
                onOpenChange={(open) => setShowMap(open)}
                open={showMap}>
                <div className='flex flex-col md:flex-row h-full relative'>
                    <div className='absolute top-0 right-0 p-10 pointer-events-none z-10'>
                        <button
                            className='w-[32px] h-[32px] md:h-[44px] md:w-[44px] button-primary-filled !p-0 pointer-events-auto'
                            onClick={() => setShowMap(false)}
                            type='button'>
                            <IconClose />
                        </button>
                    </div>
                    <div className='flex-1 relative'>
                        <GoogleMapReact
                            yesIWantToUseGoogleMapApiInternals
                            bootstrapURLKeys={{
                                key: getGoogleMapsKey(),
                            }}
                            defaultCenter={{
                                lat: property.coordinates.lat,
                                lng: property.coordinates.lng,
                            }}
                            defaultZoom={16}
                            onGoogleApiLoaded={({ map }) => {
                                mapRef.current = map;
                            }}
                            options={{
                                maxZoom: 17,
                                minZoom: 12,
                                mapId: 'ca9052da21cdf8f0b6ba14d4',
                                disableDefaultUI: true,
                                keyboardShortcuts: false,
                            }}>
                            <IconMarker
                                lat={property.coordinates.lat}
                                lng={property.coordinates.lng} />
                        </GoogleMapReact>
                        {/* Zoom controls */}
                        <div className='absolute bottom-0 right-0 text-center pointer-events-none z-10 p-16 flex gap-8'>
                            <button
                                aria-label='Zoom out'
                                className='pointer-events-auto button-primary-filled w-[32px] h-[32px] !p-0'
                                onClick={handleZoomOut}
                                type='button'>
                                <IconSubtract />
                            </button>
                            <button
                                aria-label='Zoom in'
                                className='pointer-events-auto button-primary-filled w-[32px] h-[32px] !p-0'
                                onClick={handleZoomIn}
                                type='button'>
                                <IconAdd />
                            </button>
                        </div>
                    </div>
                    <PropertyInfo
                        {...property}/>
                </div>
                <Title className='sr-only'>
                    {'Property shown on map'}
                </Title>
                <Description className='sr-only'>
                    {'See property on a Google Map'}
                </Description>
            </Dialog>
        </>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default PropertyDetailMapDialog;
